import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { LojaPageContainer } from "layouts/pages/loja/common";
import { debouce } from "helpers/utils";
import { setMessage, useMaterialUIController } from "context";

import ModalOrganizador from "./components/ModalOrganizador";
import TableOrganizador from "./components/TableOrganizador";
import { IListOrganizador } from "./components/interfaces";
import { deleteOrganizador, getAllOrganizador } from "services/organizador";
import {
  ORGANIZADORES_ACCENT,
  organizadoresToolbarSx,
  saveButtonSx,
} from "./organizadores.theme";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? ORGANIZADORES_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function Organizador(): JSX.Element {
  const [, dispatch] = useMaterialUIController();
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<IListOrganizador[] | null>(null);
  const [listOrganizador, setListOrganizador] = useState<IListOrganizador[]>([]);
  const [selected, setSelected] = useState<IListOrganizador | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const isFiltered = searchResults !== null;
  const displayedList = searchResults ?? listOrganizador;

  const stats = useMemo(() => {
    const totalEventos = listOrganizador.reduce(
      (acc, org) => acc + (org._count?.eventos ?? 0),
      0,
    );
    return { totalEventos };
  }, [listOrganizador]);

  const loadOrganizadores = useCallback(async () => {
    const resp = await getAllOrganizador();
    setListOrganizador(resp.data);
  }, []);

  useEffect(() => {
    void loadOrganizadores();
  }, [loadOrganizadores]);

  useEffect(() => {
    setSearchResults(null);
    setSearch("");
  }, [listOrganizador.length]);

  const applySearch = useCallback(
    (term: string) => {
      const q = term.trim().toLowerCase();
      if (!q) {
        setSearchResults(null);
        return;
      }
      setSearchResults(
        listOrganizador.filter(
          (item) =>
            item.nomeFantasia?.toLowerCase().includes(q) ||
            item.razaoSocial?.toLowerCase().includes(q) ||
            item.email?.toLowerCase().includes(q),
        ),
      );
    },
    [listOrganizador],
  );

  const debouncedSearch = useMemo(
    () => debouce(applySearch, 300),
    [applySearch],
  );

  const handleDeleteOrganizador = async (id: number) => {
    setListOrganizador((prev) => prev.filter((item) => item.id !== id));
    await deleteOrganizador(id);
    setMessage(dispatch, { open: true, text: "Organizador excluído com sucesso" });
  };

  const handleEditOrganizador = (organizacao: IListOrganizador) => {
    setSelected(organizacao);
    setOpenModal(true);
  };

  const updateOrganizador = (type: "edit" | "create", organizador: IListOrganizador) => {
    if (type === "create") {
      setListOrganizador((prev) => [...prev, organizador]);
    } else {
      void loadOrganizadores();
    }
  };

  const handleOpenModal = () => {
    setSelected(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelected(null);
  };

  return (
    <LojaPageContainer
      title="Organizadores"
      subtitle="Cadastro de organizadores e responsáveis pelos eventos."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={handleOpenModal}
          sx={saveButtonSx}
        >
          Novo organizador
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4}>
          <StatMini
            label={isFiltered ? "Resultados da busca" : "Total cadastrados"}
            value={isFiltered ? displayedList.length : listOrganizador.length}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatMini label="Exibindo agora" value={displayedList.length} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatMini label="Eventos vinculados" value={stats.totalEventos} />
        </Grid>
      </Grid>

      <MDBox sx={organizadoresToolbarSx}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupsOutlinedIcon sx={{ color: ORGANIZADORES_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {isFiltered
                ? `${displayedList.length} organizador${displayedList.length === 1 ? "" : "es"} no filtro`
                : `${listOrganizador.length} organizador${listOrganizador.length === 1 ? "" : "es"} cadastrado${listOrganizador.length === 1 ? "" : "s"}`}
            </MDTypography>
          </Stack>

          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel htmlFor="organizadores-search">Pesquisar organizador</InputLabel>
            <OutlinedInput
              id="organizadores-search"
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                debouncedSearch(e.target.value);
              }}
              label="Pesquisar organizador"
              placeholder="Nome, razão social ou e-mail…"
              endAdornment={
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              }
              sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
            />
          </FormControl>
        </Stack>
      </MDBox>

      {displayedList.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <GroupsOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhum organizador encontrado
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {isFiltered
              ? "Ajuste a busca ou limpe o campo para ver todos."
              : "Cadastre o primeiro organizador para vincular aos eventos."}
          </MDTypography>
        </MDBox>
      ) : (
        <TableOrganizador
          organizadores={displayedList}
          onEdit={handleEditOrganizador}
          onDelete={handleDeleteOrganizador}
        />
      )}

      <ModalOrganizador
        open={openModal}
        onClose={handleCloseModal}
        data={selected}
        updateListOrganizador={updateOrganizador}
      />
    </LojaPageContainer>
  );
}
